import React, { useState } from 'react';
import { InputNumber, Button } from 'antd';
import { LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { primaryShades } from '../../theme/theme';

/**
 * Componente per il controllo dei border radius in stile Figma/Elementor
 * Permette di impostare valori individuali per ogni angolo o un valore unico per tutti
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Funzione chiamata quando i valori cambiano
 * @param {number} props.topLeft - Valore per l'angolo in alto a sinistra
 * @param {number} props.topRight - Valore per l'angolo in alto a destra
 * @param {number} props.bottomRight - Valore per l'angolo in basso a destra
 * @param {number} props.bottomLeft - Valore per l'angolo in basso a sinistra
 * @param {string} props.prefix - Prefisso per i nomi dei campi (es. 'card', 'button', 'header')
 */
const BorderRadiusControl = ({
    onChange,
    topLeft = 0,
    topRight = 0,
    bottomRight = 0,
    bottomLeft = 0,
    prefix
}) => {
    const [linked, setLinked] = useState(
        topLeft === topRight &&
        topRight === bottomRight &&
        bottomRight === bottomLeft
    );

    const handleLinkToggle = () => {
        setLinked(!linked);
        if (!linked) {
            // Se stiamo attivando il link, impostiamo tutti i valori uguali al primo
            const value = topLeft;
            onChange({
                [`${prefix}CornerTopLeft`]: value,
                [`${prefix}CornerTopRight`]: value,
                [`${prefix}CornerBottomRight`]: value,
                [`${prefix}CornerBottomLeft`]: value
            });
        }
    };

    const handleValueChange = (position, value) => {
        // Assicuriamoci che il valore sia un numero intero valido
        const numericValue = parseInt(value) || 0;
        
        if (linked) {
            // Se i valori sono collegati, aggiorniamo tutti
            onChange({
                [`${prefix}CornerTopLeft`]: numericValue,
                [`${prefix}CornerTopRight`]: numericValue,
                [`${prefix}CornerBottomRight`]: numericValue,
                [`${prefix}CornerBottomLeft`]: numericValue
            });
        } else {
            // Altrimenti aggiorniamo solo il valore specifico
            onChange({
                [`${prefix}Corner${position}`]: numericValue
            });
        }
    };

    // Dimensione dell'anteprima
    const previewSize = 80;

    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Anteprima visiva */}
                <div style={{
                    width: previewSize,
                    height: previewSize,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    flexShrink: 0
                }}>
                    {/* Indicatori per gli angoli */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 20,
                        height: 20,
                        borderTop: `2px solid ${primaryShades.primary300}`,
                        borderLeft: `2px solid ${primaryShades.primary300}`,
                        borderTopLeftRadius: `${topLeft}px`,
                        pointerEvents: 'none'
                    }}></div>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 20,
                        height: 20,
                        borderTop: `2px solid ${primaryShades.primary300}`,
                        borderRight: `2px solid ${primaryShades.primary300}`,
                        borderTopRightRadius: `${topRight}px`,
                        pointerEvents: 'none'
                    }}></div>
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 20,
                        height: 20,
                        borderBottom: `2px solid ${primaryShades.primary300}`,
                        borderRight: `2px solid ${primaryShades.primary300}`,
                        borderBottomRightRadius: `${bottomRight}px`,
                        pointerEvents: 'none'
                    }}></div>
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: 20,
                        height: 20,
                        borderBottom: `2px solid ${primaryShades.primary300}`,
                        borderLeft: `2px solid ${primaryShades.primary300}`,
                        borderBottomLeftRadius: `${bottomLeft}px`,
                        pointerEvents: 'none'
                    }}></div>
                </div>

                <div style={{ flex: 1 }}>
                    <div style={{
                        display: 'flex',
                        marginBottom: '4px',
                        paddingRight: '40px',
                        width: '268px'
                    }}>
                        <div style={{ width: '25%', fontSize: '11px', color: '#888' }}>TOP</div>
                        <div style={{ width: '25%', fontSize: '11px', color: '#888' }}>RIGHT</div>
                        <div style={{ width: '25%', fontSize: '11px', color: '#888' }}>BOTTOM</div>
                        <div style={{ width: '25%', fontSize: '11px', color: '#888' }}>LEFT</div>
                    </div>

                    {/* Controllo compatto in stile Figma/Elementor */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        width: '300px',
                        maxWidth: '100%'
                    }}>
                        {/* Top */}
                        <div style={{
                            flex: 1,
                            borderRight: '1px solid #d9d9d9',
                        }}>
                            <InputNumber
                                min={0}
                                max={50}
                                value={topLeft}
                                onChange={(value) => handleValueChange('TopLeft', value)}
                                style={{ width: '100%', border: 'none', padding: 5, textAlign: 'center' }}
                                controls={false}
                                size="small"
                            />
                        </div>

                        {/* Right */}
                        <div style={{
                            flex: 1,
                            borderRight: '1px solid #d9d9d9',
                        }}>
                            <InputNumber
                                min={0}
                                max={50}
                                value={topRight}
                                onChange={(value) => handleValueChange('TopRight', value)}
                                style={{ width: '100%', border: 'none', padding: 5, textAlign: 'center' }}
                                disabled={linked}
                                controls={false}
                                size="small"
                            />
                        </div>

                        {/* Bottom */}
                        <div style={{
                            flex: 1,
                            borderRight: '1px solid #d9d9d9',
                        }}>
                            <InputNumber
                                min={0}
                                max={50}
                                value={bottomRight}
                                onChange={(value) => handleValueChange('BottomRight', value)}
                                style={{ width: '100%', border: 'none', padding: 5, textAlign: 'center' }}
                                disabled={linked}
                                controls={false}
                                size="small"
                            />
                        </div>

                        {/* Left */}
                        <div style={{
                            flex: 1,
                            borderRight: '1px solid #d9d9d9',
                        }}>
                            <InputNumber
                                min={0}
                                max={50}
                                value={bottomLeft}
                                onChange={(value) => handleValueChange('BottomLeft', value)}
                                style={{ width: '100%', border: 'none', padding: 5, textAlign: 'center' }}
                                disabled={linked}
                                controls={false}
                                size="small"
                            />
                        </div>

                        {/* Link button */}
                        <Button
                            icon={linked ? <LinkOutlined /> : <DisconnectOutlined />}
                            onClick={handleLinkToggle}
                            type={linked ? "primary" : "default"}
                            style={{
                                height: '100%',
                                borderRadius: 0,
                                border: 'none',
                                borderLeft: '1px solid #d9d9d9',
                                width: '32px',
                                padding: 8,
                                backgroundColor: linked ? primaryShades.primary300 : '#f5f5f5'
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BorderRadiusControl;
